<template>
  <div class="products-table-container">
    <v-data-table
      :headers="headers"
      :items="isGroup ? products : filteredProducts"
      :items-per-page="20"
      :footer-props="{ itemsPerPageOptions: [10, 20] }"
      :loading="loading"
      :search="search"
    >
      <template v-if="isAutoDistributedCampaign" v-slot:[`header.eligible_clients`]="{ header }">
        <div class="d-flex align-center">
          <span>{{ header.text }}</span>
          <mf-tooltip top max-width="300px" variant="secondary">
            <template #activator="{ on }">
              <mf-icon small color="cinza-0" class="ml-1" v-on="on">info</mf-icon>
            </template>
            <span>Quantidade de clientes selecionados para esse produto na distribuição inteligente.</span>
          </mf-tooltip>
        </div>
      </template>

      <template v-slot:[`item.image`]="{ item }">
        <div v-if="shouldShowImage(item, isGroup)" class="img-container d-flex align-center justify-center">
          <img v-if="getItemImage(item)" :src="getItemImage(item)" :alt="`Imagem do produto ${item.name}`" width="100%" />
          <mf-icon v-else>image_not_supported</mf-icon>
        </div>
      </template>

      <template v-slot:[`item.name`]="{ item }">
        <field-toggle
          v-if="!isMariaCampaignFinished"
          v-model="item.name"
          :error="($v.products.$error && $v.products.$each[getIndex(item)].name.$invalid) || item.name === ''"
          :text="true"
          :column="0"
          :row="getIndex(item)"
          :disabled="disabled || disableFieldToGroup(item)"
          @input="handleInput(item)"
        />
        <span v-else>{{ item.name }}</span>
      </template>

      <template v-slot:[`item.ean`]="{ item }">
        <span v-if="getEans(item) && getEans(item).length <= 1">{{ item.ean || '-' }}</span>
        <popover-edit
          v-else-if="getEans(item)"
          ref="popoverMenu"
          :title="item.name"
          subtitle="Múltiplos EANs"
          :disabled="disabled"
          :clickable-text="`${item.selectedEans ? item.selectedEans.length : 0}/${getEans(item).length}`"
          use-close-action
        >
          <div class="product-eans-section">
            <span class="eans-title">EANs</span>
            <div class="custom-divider" />
            <div v-for="ean in getEans(item)" :key="ean">
              <v-checkbox
                v-model="item.selectedEans"
                class="reset-margin"
                :value="ean"
                color="marca-0-l20"
                dense
                hide-details
                :disabled="
                  ((item.selectedEans ? item.selectedEans.length : 0) <= 1 && item.selectedEans ? item.selectedEans.includes(ean) : false) ||
                  hasOffersSchedulesSelected
                "
              >
                <template v-slot:label>
                  <span class="selectable-text">{{ ean }}</span>
                </template>
              </v-checkbox>
              <div class="custom-divider" />
            </div>
          </div>
        </popover-edit>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.length`]="{ item }">
        <mf-tooltip v-if="!disableFieldToGroup(item)" top variant="secondary" :disabled="item.name ? item.name.length <= 30 : false" max-width="350">
          <template #activator>
            <div class="product-name-length">
              <mf-icon size="20" fill :color="getProductNameColorAndIcon(item).color" :icon="getProductNameColorAndIcon(item).icon" />
              <span>{{ `${item.name.length} caracter${item.name.length !== 1 ? 'es' : ''}` }}</span>
            </div>
          </template>
          <span
            >Produtos com o nome maior que 30 caracteres pode limitar sua mensagem e consumir mais créditos em alguns canais, por isso é recomendado nomes
            menores.</span
          >
        </mf-tooltip>
      </template>

      <template v-if="isAutoDistributedCampaign" v-slot:[`item.eligible_clients`]="{ item }">
        <mf-tooltip top max-width="200px" variant="secondary">
          <template #activator>
            <span>{{ item.eligible_clients ? item.eligible_clients.total : '-' }}</span>
          </template>
          <div v-if="item.eligible_clients" style="display: flex; flex-direction: column; gap: 8px">
            <span class="tooltip-title">Distribuição nos critérios</span>
            <div v-if="hasDistributionModel(item, 'REPURCHASE')">
              <span class="tooltip-text">Recompra:</span>
              <span class="tooltip-value">
                {{ item.eligible_clients.repurchase_total }}
              </span>
            </div>
            <div v-if="hasDistributionModel(item, 'RECOVERY')">
              <span class="tooltip-text">Resgate:</span>
              <span class="tooltip-value">
                {{ item.eligible_clients.recovery_total }}
              </span>
            </div>
            <div v-if="hasDistributionModel(item, 'ACTIVATION')">
              <span class="tooltip-text">Ativação:</span>
              <span class="tooltip-value">
                {{ item.eligible_clients.activation_total }}
              </span>
            </div>
          </div>
        </mf-tooltip>
      </template>

      <template v-slot:[`item.products`]="{ item }">
        <div class="product-name">
          <span v-if="item.target_products.length === 1">{{ item.target_products[0].name }}</span>
          <span v-else class="link" @click="openProductsDialog(item)">{{ item.target_products.length }} produtos</span>
        </div>
      </template>

      <template v-slot:[`item.price_1`]="{ item }">
        <div v-if="hasOffersSchedulesSelected">
          <span v-if="!item.price_1 && !item.price" class="empty-value">Vazio</span>
          <span v-else>R$ {{ localeString(item.price_1 || item.price || 0, 2) }}</span>
        </div>
        <span v-else-if="isGroup && item.group_name && disableFieldToGroup(item)"> R$ {{ localeString(products[0].price_1 || 0, 2) }} </span>
        <field-toggle
          v-else
          :key="getProductPriceKey(item, 'price_1')"
          v-model="item.price_1"
          money
          :error="$v.products.$each[getIndex(item)].price_1.$invalid"
          :column="1"
          :row="getIndex(item)"
          :disabled="disabled || disableFieldToGroup(item)"
        />
      </template>

      <template v-slot:[`item.price_2`]="{ item }">
        <div v-if="hasOffersSchedulesSelected">
          <span v-if="!item.price_2" class="empty-value">Vazio</span>
          <span v-else>R$ {{ localeString(item.price_2 || 0, 2) }}</span>
        </div>
        <span v-else-if="isGroup && item.group_name && disableFieldToGroup(item)"> R$ {{ localeString(products[0].price_2 || 0, 2) }} </span>
        <field-toggle
          v-else
          :key="getProductPriceKey(item, 'price_2')"
          v-model="item.price_2"
          money
          :error="$v.products.$each[getIndex(item)].price_2.$invalid"
          :column="2"
          :row="getIndex(item)"
          :disabled="disabled || disableFieldToGroup(item)"
        />
      </template>

      <template v-slot:[`item.minimum_per_client`]="{ item }">
        <div v-if="hasOffersSchedulesSelected">
          <span>{{ item.minimum_per_client || 0 }}</span>
        </div>
        <span v-else-if="isGroup && item.group_name && disableFieldToGroup(item)"> {{ item.minimum_per_client }} </span>
        <field-toggle
          v-else
          :key="getProductPriceKey(item, 'minimum_per_client')"
          v-model="item.minimum_per_client"
          :error="$v.products.$each[getIndex(item)].minimum_per_client.$invalid"
          :column="1"
          :row="getIndex(item)"
          :disabled="disabled || disableFieldToGroup(item)"
        />
      </template>

      <template v-slot:[`item.maximum_per_client`]="{ item }">
        <div v-if="hasOffersSchedulesSelected">
          <span>{{ item.maximum_per_client || 0 }}</span>
        </div>
        <span v-else-if="isGroup && item.group_name && disableFieldToGroup(item)"> {{ item.maximum_per_client }} </span>
        <field-toggle
          v-else
          :key="getProductPriceKey(item, 'maximum_per_client')"
          v-model="item.maximum_per_client"
          :error="$v.products.$each[getIndex(item)].maximum_per_client.$invalid"
          :column="1"
          :row="getIndex(item)"
          :disabled="disabled || disableFieldToGroup(item)"
        />
      </template>

      <template v-slot:[`item.cashback_percentage`]="{ item }">
        <span>{{ item.cashback_percentage | localeString(0) | percent }}</span>
      </template>

      <template v-slot:[`item.percentage_discount`]="{ item }">
        <span>{{ item.percentage_discount | localeString(0) | percent }}</span>
      </template>

      <template v-slot:[`item.cashback_absolute`]="{ item }">
        <span>{{ item.cashback_absolute | localeString(2) | currency }}</span>
      </template>

      <template v-slot:[`item.additional_points_rate`]="{ item }">
        <span v-if="item.additional_points_rate">{{ item.additional_points_rate | localeString(0) }}%</span>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.take`]="{ item }">
        <span>{{ item.take }}</span>
      </template>
      <template v-slot:[`item.pay`]="{ item }">
        <span>{{ item.pay }}</span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <mf-toggle
          v-if="!isGroup || disableFieldToGroup(item)"
          v-model="item.active"
          :disabled="disabled || hasOffersSchedulesSelected || isMariaCampaignFinished"
          class="pa-0 ml-2"
          hide-details
        />
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <mf-tooltip v-if="!disableFieldToGroup(item)" bottom :disabled="!isRemoveButtonDisabled(item)">
          <template #activator>
            <mf-button
              type="icon"
              icon="delete"
              color="cinza-l30"
              fill
              :disabled="isRemoveButtonDisabled(item)"
              @click="isGroup ? removeGroup(item) : removeItem(item)"
            />
          </template>
          <span v-if="disableToRemoveRemainingProduct">É necessário existir ao menos um produto na campanha para continuar para o próximo passo.</span>
          <span v-else-if="isCampaignActivated">Não é possível remover produtos de uma campanha ativa ou agendada.</span>
          <span v-else>Não é possível remover produtos que foram adicionados antes da campanha ser ativada.</span>
        </mf-tooltip>
      </template>
    </v-data-table>

    <chip-list-dialog
      v-model="openComboProductsDialog"
      title="Produtos"
      use-link
      :items="comboProductsDialogItems"
      :disabled="disabled"
      :headers="comboProductsHeaders"
      searchPlaceholder="Buscar produto"
      link-url="/produtos/detalhes-produto/"
      disable-items-selection
    />
  </div>
</template>

<script>
import { mariaCampaign } from '../../../mixins/maria-campaign'
import ChipListDialog from '../../../../../../components/ChipListDialog.vue'
import FieldToggle from '../../../../../campanhas/gestao-ofertas-v2/views/components/FieldToggle.vue'
import findIndex from 'lodash/findIndex'
import cloneDeep from 'lodash/cloneDeep'
import { localeString, putEllipsis } from '@/config/helpers.js'

import { required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { price1Validation, price2Validation, minMaxPerClientValidation } from '@/modules/campanhas/gestao-ofertas-v2/helpers/validators.js'

import PopoverEdit from '../../../../../industria/ofertas-trade/views/components/PopoverEdit.vue'

export default {
  name: 'ProductsTable',
  components: {
    ChipListDialog,
    FieldToggle,
    PopoverEdit,
  },
  mixins: [mariaCampaign],
  props: {
    items: {
      type: [Object, Array],
      default: () => {},
    },
    type: {
      type: String,
      default: '',
    },
    search: {
      type: String,
      default: '',
    },
    hasOffersSchedulesSelected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disableToRemoveRemainingProduct: {
      type: Boolean,
      default: false,
    },
    isGroup: {
      type: Boolean,
      default: false,
    },
    initiallyProducts: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    loading: false,
    products: [],
    openComboProductsDialog: false,
    comboProductsDialogItems: [],
    comboProductsHeaders: [
      {
        text: '',
        value: 'image',
        width: '20%',
      },
      {
        text: 'Nome',
        value: 'name',
        width: '80%',
      },
    ],
    isUpdatingFromWatcher: false,
  }),
  computed: {
    filteredProducts() {
      const filtered = this.products.filter((product) => !product.group_rules_exhibition)
      return filtered
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    isAutoDistributedCampaign() {
      if (!Array.isArray(this.campaign?.offer_schedules) || this.campaign.offer_schedules.length === 0) {
        return false
      }
      return this.campaign.offer_schedules.some((schedule) => {
        return schedule?.auto_distributed === true || schedule?.offer_schedule?.auto_distributed === true
      })
    },
    defaultHeaders() {
      return [
        {
          text: '',
          value: 'image',
          width: '5%',
          sortable: false,
        },
        {
          text: 'SKU',
          value: 'sku',
          width: '5%',
          sortable: false,
        },
        {
          text: 'EAN',
          value: 'ean',
          width: '10%',
          sortable: false,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '35%',
          sortable: false,
        },
        {
          text: 'Tamanho',
          value: 'length',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          width: '2.5%',
        },
        {
          text: '',
          value: 'action',
          width: '2.5%',
          sortable: false,
        },
      ]
    },
    headers() {
      let headers = cloneDeep(this.defaultHeaders)
      if (this.type === 'offer_sales') {
        headers.splice(5, 0, ...this.offerSalesHeaders)
      } else if (['points_offer_sales', 'cashback_absolute_offer_sales', 'cashback_percentage_offer_sales'].includes(this.type)) {
        headers.splice(5, 0, ...this.cashbackAndPointsHeaders)
      } else if (this.type === 'percentage_offer_sales') {
        headers.splice(5, 0, ...this.percentageHeaders)
      } else if (this.type === 'takepay_offer_sales') {
        headers.splice(5, 0, ...this.takepayHeaders)
      } else {
        headers.splice(1, 4, ...this.comboHeaders)
      }
      return headers
    },
    offerSalesHeaders() {
      const headers = [
        {
          text: 'Preço 1',
          value: 'price_1',
          width: '11%',
          sortable: false,
        },
        {
          text: this.campaign.segments && this.campaign.segments.length ? 'Preço 3' : 'Preço 2',
          value: 'price_2',
          width: '11%',
          sortable: false,
        },
        {
          text: 'Mín. CPF',
          value: 'minimum_per_client',
          width: '5%',
          sortable: false,
        },
        {
          text: 'Máx. CPF',
          value: 'maximum_per_client',
          width: '5%',
          sortable: false,
        },
      ]
      if (this.isAutoDistributedCampaign) {
        headers.unshift({
          text: 'Clientes Elegíveis',
          value: 'eligible_clients',
          width: '12%',
          sortable: false,
        })
      }
      return headers
    },
    cashbackAndPointsHeaders() {
      let cashbackOrPoints = {
        width: '9%',
        sortable: false,
      }
      if (this.type === 'cashback_percentage_offer_sales') {
        cashbackOrPoints.value = 'cashback_percentage'
        cashbackOrPoints.text = 'Cashback (%)'
      } else if (this.type === 'cashback_absolute_offer_sales') {
        cashbackOrPoints.value = 'cashback_absolute'
        cashbackOrPoints.text = 'Cashback (R$)'
      } else if (this.type === 'points_offer_sales') {
        cashbackOrPoints.value = 'additional_points_rate'
        cashbackOrPoints.text = 'Pontos'
      }

      let headers = [
        {
          text: 'Preço 1',
          value: 'price_1',
          width: '9%',
          sortable: false,
        },
        { ...cashbackOrPoints },
        {
          text: 'Mín. CPF',
          value: 'minimum_per_client',
          width: '5%',
          sortable: false,
        },
        {
          text: 'Máx. CPF',
          value: 'maximum_per_client',
          width: '5%',
          sortable: false,
        },
      ]
      return headers
    },
    percentageHeaders() {
      const headers = [
        {
          text: 'Desconto',
          value: 'percentage_discount',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Mín. CPF',
          value: 'minimum_per_client',
          width: '5%',
          sortable: false,
        },
        {
          text: 'Máx. CPF',
          value: 'maximum_per_client',
          width: '5%',
          sortable: false,
        },
      ]
      if (this.isAutoDistributedCampaign) {
        headers.unshift({
          text: 'Clientes Elegíveis',
          value: 'eligible_clients',
          width: '12%',
          sortable: false,
        })
      }
      return headers
    },
    takepayHeaders() {
      const headers = [
        {
          text: 'Leve',
          value: 'take',
          width: '12.5%',
          sortable: false,
        },
        {
          text: 'Pague',
          value: 'pay',
          width: '12.5%',
          sortable: false,
        },
        {
          text: 'Máx. CPF',
          value: 'maximum_per_client',
          width: '5%',
          sortable: false,
        },
      ]
      if (this.isAutoDistributedCampaign) {
        headers.unshift({
          text: 'Clientes Elegíveis',
          value: 'eligible_clients',
          width: '12%',
          sortable: false,
        })
      }
      return headers
    },
    comboHeaders() {
      const headers = [
        {
          text: 'Nome do combo',
          value: 'name',
          width: '30%',
          sortable: false,
        },
        {
          text: 'Tamanho',
          value: 'length',
          width: '20%',
          sortable: false,
        },
        {
          text: `Produto${this.products?.target_products?.length > 1 ? 's' : ''} com desconto`,
          value: 'products',
          width: '25%',
          sortable: false,
        },
        {
          text: 'Máx. CPF',
          value: 'maximum_per_client',
          width: '5%',
          sortable: false,
        },
      ]
      if (this.isAutoDistributedCampaign) {
        headers.splice(2, 0, {
          text: 'Clientes Elegíveis',
          value: 'eligible_clients',
          width: '12%',
          sortable: false,
        })
      }
      return headers
    },
  },
  watch: {
    products: {
      deep: true,
      handler(newProducts, oldProducts) {
        if (this.isUpdatingFromWatcher) {
          return
        }

        if (newProducts && newProducts.length > 0) {
          this.isUpdatingFromWatcher = true
          this.handleProductsChange(newProducts)
          this.$nextTick(() => {
            this.isUpdatingFromWatcher = false
          })
        }
      },
    },
  },
  mounted() {
    let products = this.items?.map((product) => {
      let processedProduct = {}

      if (product?.selectedEans) {
        processedProduct = { ...product }
      } else {
        processedProduct = {
          ...product,
          selectedEans: product.eans,
        }
      }

      if (!processedProduct.eligible_clients) {
        if (processedProduct.offer_schedule_id) {
          const eligibleClients = this.getEligibleClientsFromOfferSchedule(processedProduct)
          if (eligibleClients) {
            processedProduct.eligible_clients = eligibleClients
          }
        }
        if (
          !processedProduct.eligible_clients &&
          processedProduct?.offer_sales &&
          Array.isArray(processedProduct.offer_sales) &&
          processedProduct.offer_sales.length > 0
        ) {
          const firstOfferSale = processedProduct.offer_sales[0]
          if (firstOfferSale?.eligible_clients) {
            processedProduct.eligible_clients = firstOfferSale.eligible_clients
          }
        }
      }
      return processedProduct
    })

    if (this.isGroup && products && products.length > 0) {
      const groupItem = products[0]
      if (groupItem && groupItem.groupInfo?._id) {
        products.forEach((product, index) => {
          if (index > 0) {
            product.groupInfo = {
              _id: groupItem.groupInfo._id,
              __typename: 'ProductsGroup',
            }
          }
        })
      }
    }
    this.products = products
  },
  methods: {
    localeString,
    putEllipsis,
    shouldShowImage(item, isGroup) {
      return !isGroup || (!this.disableFieldToGroup(item) && item.group_image_url && item.group_rules_exhibition === 'PUBLIC')
    },
    getItemImage(item) {
      let groupImage = ''
      if (!this.disableFieldToGroup(item) && item.group_image_url && item.group_rules_exhibition === 'PUBLIC') {
        groupImage = item.group_image_url
      }

      const directImage = item.image || item.image_url

      const nestedImage = item.product?.image || item.product?.image_url

      return groupImage || directImage || nestedImage
    },
    getEans(product) {
      if (product?.product?.eans) {
        return product?.product?.eans || []
      }
      return product?.eans || []
    },
    handleProductsChange(newProducts) {
      let selectedOffersSchedules = cloneDeep(this.campaign.offer_schedules)
      if (!this.hasOffersSchedulesSelected) {
        this.handleNoOffersSchedulesSelected(newProducts)
      } else {
        this.setOmniCampaignKey({ key: 'offer_schedules', payload: selectedOffersSchedules })
      }
    },
    handleNoOffersSchedulesSelected(newProducts) {
      if (this.isGroup) {
        this.handleGroupProducts(newProducts)
      } else {
        this.handleSoloProducts(newProducts)
      }
    },
    handleSoloProducts(newProducts) {
      const newProductsWithoutGroup = newProducts.filter((product) => !product.groupInfo)
      const groupProducts = this.campaign.offer_sales.filter((product) => product.groupInfo)

      const updatedSoloProducts = this.campaign.offer_sales
        .filter((product) => !product.groupInfo)
        .map((existingProduct) => {
          const newValueProduct = newProductsWithoutGroup.find((p) => p.sku === existingProduct.sku)
          if (newValueProduct) {
            return {
              ...existingProduct,
              name: newValueProduct.name,
              active: newValueProduct.active,
              eans: newValueProduct.selectedEans || existingProduct.eans,
              price_1: newValueProduct.price_1,
              price_2: newValueProduct.price_2,
              maximum_per_client: newValueProduct.maximum_per_client,
              minimum_per_client: newValueProduct.minimum_per_client,
            }
          }
          return existingProduct
        })

      const updatedProducts = [...updatedSoloProducts, ...groupProducts]
      this.setOmniCampaignKey({ key: 'offer_sales', payload: updatedProducts })
    },
    handleGroupProducts(newProducts) {
      const groupItem = newProducts[0]

      if (!groupItem || !groupItem.groupInfo?._id) {
        return
      }

      const groupId = groupItem.groupInfo._id

      const productsInGroup = this.campaign.offer_sales.filter((product) => {
        const productGroupId = product.groupInfo?._id || product.group_id || product._id
        return productGroupId === groupId
      })

      productsInGroup.forEach((product) => {
        const newValueProduct = this.findNewValueProduct(newProducts, product)
        if (newValueProduct) {
          product.active = newValueProduct?.active
          product.eans = newValueProduct?.selectedEans || product.eans
          product.price_1 = groupItem.price_1
          product.price_2 = groupItem.price_2
          product.maximum_per_client = groupItem.maximum_per_client
          product.minimum_per_client = groupItem.minimum_per_client
          product.group_name = groupItem.name
        }
      })

      this.setOmniCampaignKey({ key: 'offer_sales', payload: this.campaign.offer_sales })
    },
    handleInput(item, action) {
      if (this.campaignHasOffers) {
        let index_offer_schedule = 0
        let index_offer = 0

        let selectedOffersSchedules = cloneDeep(this.campaign.offer_schedules)

        if (item.groupInfo?._id) {
          const itemGroupId = item.groupInfo._id
          selectedOffersSchedules.map((offerSchedule, indexOfferSchedule) => {
            offerSchedule[this.type].map((offer, indexOffer) => {
              const offerGroupId = offer.groupInfo?._id || offer.group_id || offer._id
              if (offerGroupId === itemGroupId) {
                index_offer_schedule = indexOfferSchedule
                index_offer = indexOffer
              }
            })
          })
        } else {
          selectedOffersSchedules.map((offerSchedule, indexOfferSchedule) => {
            offerSchedule[this.type].map((offer, indexOffer) => {
              if (offer.sku === item.sku) {
                index_offer_schedule = indexOfferSchedule
                index_offer = indexOffer
              }
            })
          })
        }

        if (!item.sku && item.group_id && action !== 'remove') {
          selectedOffersSchedules[index_offer_schedule][this.type].forEach((product) => {
            if (product?.group_id === item?.group_id) {
              product.group_name = item.name
            }
          })
          this.setOmniCampaignKey({ key: 'offer_schedules', payload: selectedOffersSchedules })
          return
        }

        if (
          selectedOffersSchedules[index_offer_schedule] &&
          selectedOffersSchedules[index_offer_schedule][this.type] &&
          selectedOffersSchedules[index_offer_schedule][this.type][index_offer]
        ) {
          if (action === 'remove') {
            selectedOffersSchedules[index_offer_schedule][this.type].splice(index_offer, 1)
          } else {
            selectedOffersSchedules[index_offer_schedule][this.type][index_offer].name = item.name
          }

          this.setOmniCampaignKey({ key: 'offer_schedules', payload: selectedOffersSchedules })
        }
      } else {
        const updatedOfferSales = this.campaign.offer_sales.map((product) => {
          if (product.sku === item.sku) {
            return {
              ...product,
              name: item.name,
            }
          }

          if (item.groupInfo?._id && product.groupInfo?._id) {
            const itemGroupId = item.groupInfo._id
            const productGroupId = product.groupInfo._id || product.group_id || product._id
            if (productGroupId === itemGroupId) {
              return {
                ...product,
                group_name: item.name,
              }
            }
          }

          return product
        })

        this.setOmniCampaignKey({ key: 'offer_sales', payload: updatedOfferSales })
      }
    },
    findNewValueProduct(newValue, product) {
      if (product.groupInfo) {
        const productGroupId = product.groupInfo?._id || product.group_id || product._id
        return newValue.find((p) => {
          const newValueGroupId = p.groupInfo?._id || p.group_id || p._id
          return p.sku === product.sku && newValueGroupId === productGroupId
        })
      }
      return newValue.find((p) => p.sku === product.sku)
    },
    disableFieldToGroup(item) {
      if (!this.isGroup) {
        return false
      }

      if (this.products && this.products.length > 0) {
        const firstItem = this.products[0]
        const isFirstItem = firstItem === item || (firstItem && item && firstItem.sku === item.sku && firstItem.name === item.name)

        if (isFirstItem) {
          return false
        } else {
          return true
        }
      }

      return false
    },
    removeGroup(item) {
      this.handleInput(item, 'remove')
      if (!this.hasOffersSchedulesSelected) {
        const selectedProducts = cloneDeep(this.campaign.offer_sales).filter((product) =>
          Boolean(product?.groupInfo ? product.groupInfo._id !== item._id : product)
        )
        const importedProducts = cloneDeep(this.importedProducts).filter((product) => (product?.groupInfo ? product.groupInfo._id !== item._id : product))
        const searchedProducts = cloneDeep(this.searchedProducts).filter((product) => (product?.groupInfo ? product.groupInfo._id !== item._id : product))

        this.setOmniCampaignKey({ key: 'offer_sales', payload: selectedProducts })
        this.$store.commit('SET_IMPORTED_PRODUCTS', importedProducts)
        this.$store.commit('SET_SEARCHED_PRODUCTS', searchedProducts)
      } else {
        let selectedOffersSchedules = this.campaign.offer_schedules

        selectedOffersSchedules = selectedOffersSchedules.map((offerSchedule) => {
          return {
            ...offerSchedule,
            [this.type]: offerSchedule[this.type].filter((product) => {
              if (product?._id) return product._id !== item._id
              return true
            }),
          }
        })

        this.setOmniCampaignKey({ key: 'offer_schedules', payload: selectedOffersSchedules })
      }
    },
    removeItem(item) {
      this.handleInput(item, 'remove')

      if (!this.hasOffersSchedulesSelected) {
        this.removeItemFromOfferSales(item)
      } else {
        this.removeItemFromOfferSchedules(item)
      }
    },
    removeItemFromOfferSales(item) {
      const selectedProducts = cloneDeep(this.campaign.offer_sales).filter((product) => product.sku !== item.sku)
      this.setOmniCampaignKey({ key: 'offer_sales', payload: selectedProducts })
      this.products = selectedProducts
    },
    removeItemFromOfferSchedules(item) {
      this.products = this.products.filter((product) => {
        if (product.sku !== item.sku) return true

        const isFromGroup = (value) => !!value?.groupInfo || !!value?.isGroup || (!!value?._id && !!value?.group_id)
        const getGroupId = (value) => (value?.groupInfo ? value?.groupInfo._id : value?._id)

        const valuesFromGroups = isFromGroup(product) && isFromGroup(item)

        if (!valuesFromGroups) return false

        const groupIdVerification = getGroupId(item) !== getGroupId(product)
        const offerScheduleVerification = this.getOfferScheduleVerification(item, product)

        return groupIdVerification || offerScheduleVerification
      })
    },
    getOfferScheduleVerification(item, product) {
      return item?.offer_schedule_id && product?.offer_schedule_id ? item.offer_schedule_id !== product.offer_schedule_id : true
    },
    validateProductsData() {
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        return false
      }

      return true
    },
    openProductsDialog(item) {
      this.openComboProductsDialog = !this.openComboProductsDialog
      this.comboProductsDialogItems = item.target_products
    },
    getIndex(item) {
      const index = findIndex(this.products, (product) => {
        if (item?.groupInfo?._id) {
          const itemGroupId = item.groupInfo._id
          const productGroupId = product.groupInfo?._id || product.group_id || product._id
          return productGroupId === itemGroupId && product._id === item._id
        } else return product.sku === item.sku
      })
      return index
    },
    getProductNameColorAndIcon(product) {
      const nameLength = product?.name?.length

      if (!nameLength) return { color: 'cinza-d10', icon: 'error' }
      if (nameLength <= 20) return { color: 'verde-l10', icon: 'check_circle' }
      if (nameLength <= 30) return { color: '#F3AF00', icon: 'do_not_disturb_on' }
      return { color: 'vermelho-d20', icon: 'error' }
    },
    getProductPriceKey(item, key) {
      if (item?.groupInfo?._id) {
        let productPriceKey = `${item.name}_${item.groupInfo._id}_${key}`
        if (this.isGroup) {
          productPriceKey = productPriceKey + `_group_${key}`
        }
        return productPriceKey
      } else if (item._id) {
        return `${item.name}_${item._id}_${key}`
      } else return `${item.name}_${item.sku}_${key}`
    },
    isRemoveButtonDisabled(item) {
      if (this.isCampaignActivated) {
        return item.disabled || this.disabled || this.disableToRemoveRemainingProduct || this.productWasAddedAfterCampaignActivation(item)
      } else {
        return this.disableToRemoveRemainingProduct
      }
    },
    productWasAddedAfterCampaignActivation(item) {
      return !this.initiallyProducts.some((product) => product.sku === item.sku)
    },
    getEligibleClientsFromOfferSchedule(product) {
      if (!product.offer_schedule_id || !this.campaign.offer_schedules) {
        return null
      }

      const offerSchedule = this.campaign.offer_schedules.find(
        (schedule) =>
          schedule.offer_schedule_id === product.offer_schedule_id ||
          schedule._id === product.offer_schedule_id ||
          schedule?.offer_schedule?._id === product.offer_schedule_id
      )

      if (!offerSchedule) {
        return null
      }

      const offerType = this.type || 'offer_sales'

      if (offerSchedule[offerType] && Array.isArray(offerSchedule[offerType])) {
        if (product.groupInfo?._id || product.group_id) {
          const groupId = product.groupInfo?._id || product.group_id

          const group = offerSchedule[offerType].find((g) => g._id === groupId || g.group_id === groupId || (g.groupInfo && g.groupInfo._id === groupId))

          if (group) {
            if (group.offer_sales && Array.isArray(group.offer_sales)) {
              const productInGroup = group.offer_sales.find((p) => p.sku === product.sku)
              if (productInGroup?.eligible_clients) {
                return productInGroup.eligible_clients
              }
            }
            if (group[offerType] && Array.isArray(group[offerType])) {
              const productInGroup = group[offerType].find((p) => p.sku === product.sku)
              if (productInGroup?.eligible_clients) {
                return productInGroup.eligible_clients
              }
            }
          }
        }

        const productInSchedule = offerSchedule[offerType].find((p) => p.sku === product.sku && !p.group_id && !p.groupInfo)
        if (productInSchedule?.eligible_clients) {
          return productInSchedule.eligible_clients
        }

        for (const item of offerSchedule[offerType]) {
          if (item.offer_sales && Array.isArray(item.offer_sales)) {
            const productInGroup = item.offer_sales.find((p) => p.sku === product.sku)
            if (productInGroup?.eligible_clients) {
              return productInGroup.eligible_clients
            }
          }
          if (item[offerType] && Array.isArray(item[offerType])) {
            const productInGroup = item[offerType].find((p) => p.sku === product.sku)
            if (productInGroup?.eligible_clients) {
              return productInGroup.eligible_clients
            }
          }
        }
      }
      return null
    },
    hasDistributionModel(item, modelType) {
      if (!item.offer_schedule_id || !this.campaign.offer_schedules) {
        return false
      }

      const offerSchedule = this.campaign.offer_schedules.find(
        (schedule) =>
          schedule.offer_schedule_id === item.offer_schedule_id ||
          schedule._id === item.offer_schedule_id ||
          schedule?.offer_schedule?._id === item.offer_schedule_id
      )

      if (!offerSchedule) {
        return false
      }

      const userDistributionModels = offerSchedule.user_distribution_models || offerSchedule.offer_schedule?.user_distribution_models || []

      return userDistributionModels.some((model) => model.model === modelType)
    },
  },
  validations() {
    let fields = {}

    if (this.type === 'offer_sales') {
      fields = {
        $each: {
          sku: !this.isGroup ? { required } : {},
          name: { required, notOnlySpace },
          price_1: { price1Validation: price1Validation(this.isFranchise, true) },
          price_2: { price2Validation: price2Validation(this.isFranchise, true) },
          minimum_per_client: { minMaxPerClientValidation },
          maximum_per_client: { minMaxPerClientValidation },
        },
      }
    } else {
      fields = {
        $each: {
          sku: this.type !== 'combo_offer_sales' && !this.isGroup ? { required } : {},
          name: { required, notOnlySpace },
        },
      }
    }
    return {
      products: { ...fields },
    }
  },
}
</script>

<style lang="scss" scoped>
.products-table-container {
  .product-name-length {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }

  .img-container {
    width: 35px;
    height: 35px;

    & img {
      display: block;
      max-width: 100%;
      max-height: 100%;
    }
  }
}

// paginated-data-table style

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}
.is-disabled {
  opacity: 0.5;
  pointer-events: none;
  ::v-deep th {
    color: grey !important;
  }

  ::v-deep tbody td {
    color: grey !important;
  }
}

.product-eans-section {
  max-height: 300px;
  overflow: auto;

  .eans-title {
    font-size: 14px;
    font-weight: bold;
    color: #31363b;
  }

  .custom-divider {
    margin-top: 8px;
    border-bottom: 1px solid $cinza-l70;
  }

  .reset-margin {
    margin: 0px;
    margin-top: 8px;
  }

  .selectable-text {
    pointer-events: auto;
    user-select: text;
    cursor: text;
  }
}
.tooltip-title {
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 0.23px;
  color: $cinza-d20;
}
.tooltip-text {
  font-weight: 400;
  font-size: 12px;
  line-height: 120%;
  letter-spacing: 1%;
  color: $cinza-0;
}
.tooltip-value {
  font-weight: 700;
  line-height: 120%;
  color: $marca-0;
}
</style>
